import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Eye, EyeOff, Check, X, RefreshCw } from 'lucide-react'
import { getMailConfig, setMailConfig } from '../api/mail'
import { getBuzzConfig, setBuzzConfig } from '../api/buzz'
import { getStorageConfig, setStorageConfig } from '../api/storage'
import { getRaiderConfig, setRaiderConfig } from '../api/raider'
import { getBrainerConfig, setBrainerConfig } from '../api/brainer'
import { getInvoiceFacteurConfig, setInvoiceFacteurConfig, revealInvoiceFacteurConfig } from '../api/invoice-facteur'
import { getOrderParserConfig, setOrderParserConfig } from '../api/order-parser'
import { getEnergyOsConfig, setEnergyOsConfig } from '../api/energy-os'
import { getGarageConfig, setGarageConfig } from '../api/garage'
import { useDateFmt } from '../hooks/useDateFmt'
import { useAuth } from '../store/auth'
import type { ServiceConfigParam, ModuleKey } from '../types'

type ServiceId = 'mail' | 'buzz' | 'storage' | 'raider' | 'brainer' | 'invoice-facteur' | 'order-parser' | 'energy-os' | 'garage'

interface ServiceDef {
  id: ServiceId
  label: string
  getConfig: () => Promise<{ data: ServiceConfigParam[] }>
  setConfig: (key: string, value: string) => Promise<unknown>
  revealConfig?: (key: string) => Promise<{ data: { value: string } }>
  moduleKey?: ModuleKey
}

const SERVICES: ServiceDef[] = [
  { id: 'mail',    label: 'Mail service',    getConfig: getMailConfig,    setConfig: setMailConfig,    moduleKey: 'mail-service' },
  { id: 'buzz',    label: 'Event service',   getConfig: getBuzzConfig,    setConfig: setBuzzConfig },
  { id: 'storage', label: 'Storage service', getConfig: getStorageConfig, setConfig: setStorageConfig, moduleKey: 'storage-service' },
  { id: 'raider',  label: 'Raider agent',    getConfig: getRaiderConfig,  setConfig: setRaiderConfig,   moduleKey: 'raider-agent' },
  { id: 'brainer', label: 'Brainer',         getConfig: getBrainerConfig, setConfig: setBrainerConfig, moduleKey: 'brainer' },
  { id: 'invoice-facteur', label: 'Invoice facteur', getConfig: getInvoiceFacteurConfig, setConfig: setInvoiceFacteurConfig, revealConfig: revealInvoiceFacteurConfig, moduleKey: 'invoice-facteur' },
  { id: 'order-parser', label: 'Order parser', getConfig: getOrderParserConfig, setConfig: setOrderParserConfig, moduleKey: 'order-parser' },
  { id: 'energy-os', label: 'EnergyOS', getConfig: getEnergyOsConfig, setConfig: setEnergyOsConfig, moduleKey: 'energy-os' },
  { id: 'garage', label: 'Garage', getConfig: getGarageConfig, setConfig: setGarageConfig, moduleKey: 'garage' },
]

function generateHex32(): string {
  const arr = new Uint8Array(32)
  window.crypto.getRandomValues(arr)
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
}

function ParamRow({
  param,
  onSave,
  onGenerate,
  onReveal,
}: {
  param: ServiceConfigParam
  onSave: (key: string, value: string) => Promise<void>
  onGenerate?: (key: string) => Promise<void>
  onReveal?: (key: string) => Promise<string>
}) {
  const fmt = useDateFmt()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [showSecret, setShowSecret] = useState(false)
  const [revealedValue, setRevealedValue] = useState<string | null>(null)
  const [revealing, setRevealing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')

  const startEdit = () => { setDraft(''); setError(''); setEditing(true) }
  const cancel = () => { setEditing(false); setError('') }

  const save = async () => {
    setSaving(true); setError('')
    try {
      await onSave(param.key, draft)
      setEditing(false)
      setShowSecret(false)
      setRevealedValue(null)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const generate = async () => {
    if (!onGenerate) return
    setGenerating(true); setError('')
    try {
      await onGenerate(param.key)
      setShowSecret(false)
      setRevealedValue(null)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate')
    } finally {
      setGenerating(false)
    }
  }

  const toggleReveal = async () => {
    if (showSecret) { setShowSecret(false); return }
    if (!onReveal) { setShowSecret(true); return }
    setRevealing(true); setError('')
    try {
      const value = await onReveal(param.key)
      setRevealedValue(value)
      setShowSecret(true)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reveal')
    } finally {
      setRevealing(false)
    }
  }

  const displayValue = () => {
    if (param.isSecret) {
      if (editing) return null
      if (!param.isSet && !param.value) return <span className="text-ink-dim text-xs italic">not set</span>
      return showSecret
        ? <span className="font-mono text-xs text-ink">{revealedValue ?? param.value}</span>
        : <span className="font-mono text-xs text-ink-dim">{'•'.repeat(8)}</span>
    }
    return param.value
      ? <span className="font-mono text-xs text-ink">{param.value}</span>
      : <span className="text-ink-dim text-xs italic">not set</span>
  }

  return (
    <tr>
      <td className="align-top">
        <p className="text-sm font-mono font-medium text-ink">{param.key}</p>
        <p className="text-xs text-ink-dim mt-0.5">{param.description}</p>
      </td>
      <td className="align-middle">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type={param.isSecret ? 'password' : 'text'}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={param.isSecret ? 'Enter new value...' : 'Enter value...'}
              autoFocus
              className="max-w-xs field font-mono"
            />
            <button onClick={save} disabled={saving} className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50">
              <Check size={15} />
            </button>
            <button onClick={cancel} className="text-ink-dim hover:text-ink">
              <X size={15} />
            </button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {displayValue()}
            {param.isSecret && (param.isSet || param.value) && !editing && (
              <button
                onClick={toggleReveal}
                disabled={revealing}
                className="text-ink-dim hover:text-ink-dim transition-colors disabled:opacity-40"
                title={showSecret ? 'Hide' : 'Reveal'}
              >
                {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            )}
            {error && <span className="text-xs text-red-600 ml-1">{error}</span>}
          </div>
        )}
      </td>
      <td className="align-middle text-xs text-ink-dim">
        {fmt(param.updatedAt)}
      </td>
      <td className="align-middle">
        {param.globalOnly ? (
          <span className="text-xs text-ink-dim italic">service-global</span>
        ) : !editing ? (
          <div className="flex items-center gap-3">
            {onGenerate && (
              <button
                onClick={generate}
                disabled={generating}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors disabled:opacity-40"
                title="Generate a random value"
              >
                <RefreshCw size={11} className={generating ? 'animate-spin' : ''} />
                Generate
              </button>
            )}
            <button
              onClick={startEdit}
              className="flex items-center gap-1 text-xs text-ink-dim hover:text-ink transition-colors"
            >
              <Pencil size={12} /> Edit
            </button>
          </div>
        ) : null}
      </td>
    </tr>
  )
}

const GENERATE_KEYS: Record<string, true> = {
  FUNNEL_ENCRYPTION_KEY: true,
}

function ServicePanel({ service }: { service: ServiceDef }) {
  const qc = useQueryClient()

  const { data: params = [], isLoading } = useQuery({
    queryKey: ['service-config', service.id],
    queryFn: () => service.getConfig().then(r => r.data),
  })

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) => service.setConfig(key, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['service-config', service.id] }),
  })

  const handleSave = async (key: string, value: string) => {
    await save.mutateAsync({ key, value })
  }

  const handleGenerate = async (key: string) => {
    await save.mutateAsync({ key, value: generateHex32() })
  }

  if (isLoading) return <div className="p-8 text-center text-sm text-ink-dim">Loading...</div>

  if ((params as ServiceConfigParam[]).length === 0) return (
    <div className="p-8 text-center text-sm text-ink-dim">No configurable parameters</div>
  )

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th className="w-2/5">Parameter</th>
          <th>Value</th>
          <th>Last updated</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {(params as ServiceConfigParam[]).map(p => (
          <ParamRow
            key={p.key}
            param={p}
            onSave={handleSave}
            onGenerate={GENERATE_KEYS[p.key] ? handleGenerate : undefined}
            onReveal={service.revealConfig ? (key) => service.revealConfig!(key).then(r => r.data.value) : undefined}
          />
        ))}
      </tbody>
    </table>
  )
}

export function ServiceSettings() {
  const { user } = useAuth()
  const visibleServices = SERVICES.filter(s => !s.moduleKey || user?.enabledModules?.includes(s.moduleKey))
  const [active, setActive] = useState<ServiceId>('mail')
  const service = visibleServices.find(s => s.id === active) ?? visibleServices[0]

  return (
    <div>
      <div className="mb-3">
        <h1 className="text-xl font-semibold text-ink">Service settings</h1>
        <p className="text-sm text-ink-dim mt-0.5">Runtime configuration for each service</p>
      </div>

      <div className="flex mb-4 border-b border-paper-border">
        {visibleServices.map(s => (
          <button
            key={s.id}
            onClick={() => setActive(s.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              active === s.id
                ? 'border-accent text-accent'
                : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="table-wrap">
        <ServicePanel service={service} />
      </div>
    </div>
  )
}
